<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import { LineChart } from '@/components/ui/chart-line';
import type { Offer } from '~/chia/offer';
import { useIntervalFn } from '@vueuse/core';

const props = defineProps<{
  newOffer?: Offer & { timestamp: number };
}>();

const { newOffer } = toRefs(props);

interface OfferData {
  date: string;
  offers: number;
}

const chartData = ref<{ date: string; 'Number of Offers': number }[]>([]);

onMounted(async () => {
  try {
    const response = await fetch('https://api.dexie.space/v1/splash/stats');
    const data = await response.json();
    chartData.value = data.stats.offers.map((item: OfferData) => ({
      date: new Date(item.date).toLocaleString(undefined, {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
      'Number of Offers': item.offers,
    }));
  } catch (error) {
    console.error('Error fetching data:', error);
  }
});

useIntervalFn(() => {
  const now = new Date();
  if (now.getSeconds() === 0) {
    const formattedDate = now.toLocaleString(undefined, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

    const lastEntry = chartData.value[chartData.value.length - 1];
    if (!lastEntry || lastEntry.date !== formattedDate) {
      chartData.value.push({
        date: formattedDate,
        'Number of Offers': 0,
      });
      chartData.value.shift();
    }
  }
}, 1000);

watch(
  newOffer,
  (offer) => {
    const offerDate = new Date(offer.timestamp);
    console.log(offerDate);

    const formattedDate = offerDate.toLocaleString(undefined, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

    const existingEntryIndex = chartData.value.findIndex((entry) => entry.date === formattedDate);

    if (existingEntryIndex !== -1) {
      chartData.value[existingEntryIndex]['Number of Offers']++;
    } else {
      chartData.value.push({
        date: formattedDate,
        'Number of Offers': 1,
      });
    }
  },
  { deep: true }
);
</script>

<template>
  <Card x-chunk="dashboard-05-chunk-3">
    <div class="flex justify-between items-center">
      <CardHeader class="px-7">
        <CardTitle>Statistics</CardTitle>
        <CardDescription>Number of offers sent via Splash!</CardDescription>
      </CardHeader>
    </div>
    <CardContent>
      <LineChart
        v-if="chartData.length > 0"
        :data="chartData"
        index="date"
        class="h-48"
        :show-legend="false"
        :categories="['Number of Offers']"
      />
      <p v-else>Loading data...</p>
    </CardContent>
  </Card>
</template>
